import { Component, Input, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { Plant } from "src/model/class/general classes/plant";
import {
  BIGDeviceOperationMode,
  BIGModeReading,
} from "src/components/shared-components/enum/enumDevice";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iCredential } from "src/model/interface/icredential";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AuthenticationService } from "src/service/authentication.service";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { clientSettingsUpdateCardReadingModeAction } from "src/state/actions/client-settings.actions";

@Component({
    selector: "app-login-access-plant",
    templateUrl: "./login-access-plant.component.html",
    styleUrls: ["./login-access-plant.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginAccessPlantComponent
  implements OnInit, OnDestroy, iTranslation, iEvent
{
  private _logContext: string = "Login-access-plant.component";

  private _selectedPlantOnlineSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;

  //#region Translated strings
  private _lblConnectionStateAvaiable: string = "Connessione disponibile";
  private _lblConnecitonStateNotAvaiable: string =
    "Connessione non disponibile";
  private _lblConnectionStateWaiting: string =
    "Verifica connessione in corso...";
  //#endregion

  public _eventSubscriptions: Array<Subscription> = null;

  public isPlantConnectionAvaiable: boolean = false;
  public connectionStateClass: string = "waiting";
  public iconConnectionType: string = "globe-outline";

  public connectionStateText: string = this._lblConnectionStateWaiting;
  public srcImageLogo: string = "";
  public heightLogo: number = 0;
  public styleLogo = {};

  @Input() currentPlant: Plant;

  constructor(
    private _eventService: EventService,
    private _networkService: NetworkService,
    private _translateService: TranslateService,
    private _ngZone: NgZone,
    private _systemRouterService: SystemRouterService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _authService: AuthenticationService,
    private _themeService: ThemeService
  ) {}

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.getSrcImageLogoByApplicationTheme();

    /** Resetting connection state variable */
    this.connectionStateClass = "waiting";
    this.connectionStateText = this._lblConnectionStateWaiting;
    this.isPlantConnectionAvaiable = false;

    this.subscribeEvents();

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      " ngOnInit() ",
      "Before subscription to ._networkService.currentConnectionType"
    );

    /** Tracking selected plant connection state. Ignoring first subscription because the real state is received from second emission */
    this._selectedPlantOnlineSubscription =
      this._networkService.currentConnectionType.subscribe(
        (connectionType: number) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            " _networkService ",
            " currentConnectionType ",
            "Current connection: " + ConnectionType[connectionType]
          );
          this.normalizeConnectionState(connectionType);
        }
      );
  }

  ngOnDestroy() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      " ngOnDestroy() ",
      "DESTROY"
    );

    this._selectedPlantOnlineSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();

    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(
        () => {
          if (this._selectedPlantOnlineSubscription) {
            this._selectedPlantOnlineSubscription.unsubscribe();
          }
          if (this._translateSubscription) {
            this._translateSubscription.unsubscribe();
          }

          this.unsubscribeEvents();
        } //
      )
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Translation
  public loadTranslation() {
    this._translateService
      .get([
        "dictionary.connectionStateAvaiable",
        "dictionary.connectionStateNotAvaiable",
        "dictionary.connectionStateWaiting",
      ])
      .subscribe((translation) => {
        this._lblConnectionStateAvaiable =
          translation["dictionary.connectionStateAvaiable"];
        this._lblConnecitonStateNotAvaiable =
          translation["dictionary.connectionStateNotAvaiable"];
        this._lblConnectionStateWaiting =
          translation["dictionary.connectionStateWaiting"];

        switch (this.connectionStateClass) {
          case "waiting":
            this.connectionStateText = this._lblConnectionStateWaiting;
            break;

          case "not-avaiable":
            this.connectionStateText = this._lblConnecitonStateNotAvaiable;
            break;

          case "avaiable":
            this.connectionStateText = this._lblConnectionStateAvaiable;
            break;
          default:
            break;
        }
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }

  //#endregion

  /**
   * Handle click to comeback
   */
  public onClickComeBackToPlantList(): void {
    this._authService.unsetCurrentUserData();

    this._eventService.OnComebackToPlantList.next(null);
    // this is required when there exist a single plant and we navigated directly to the login page instead of the default plant list page

    this._systemRouterService.navigateBackToPlantListOnExistSinglePlant();
  }

  /**
   * Handle request to go to plant settings
   */
  public onRequestToNavigateToPlantSettings(event): void {
    this._eventService.OnComebackToPlantSettings.next(event);

    this._systemRouterService.navigateToSpecificPlant(this.currentPlant.ID);
  }

  /**
   * Handle callback event for login success
   *
   * @param event
   */
  public onLoginSuccess(event: iCredential): void {
    this._eventService.OnLoginPlantDid.next(event.Username);

    this._systemRouterService.navigateToHomepageReservedArea();

    // if (!this._themeService.isDigitalPlace) {
    //   if (this.currentPlant.operativeMode == BIGDeviceOperationMode.STANDARD) {
    //     this._store.dispatch(
    //       clientSettingsUpdateCardReadingModeAction({
    //         cardReadingMode: BIGModeReading.CARD_NUMBER,
    //       })
    //     );
    //   }
    // }
  }

  /**
   * This method checks if values assigned to API server and mqtt broker ports in the "plant-connection-settings" page are ammissible.
   * To be so, these values MUST be:
   *
   * 1)Numbers
   *
   * 2)0 < x < 65536
   * @returns TRUE if all the significative ports' (as determined by the "currentPlant.AllowWanConnection") values are ammissible; FALSE otherwise
   */
  public arePortStringsCorrect(): boolean {
    let returnValue: boolean = false;

    let LanPortNumber = Number(this.currentPlant.LanPort);
    let LANMqttPortNumner = Number(this.currentPlant.LANMqttPort);
    let WanPortNumber = Number(this.currentPlant.WanPort);
    let WANMqttPortNumber = Number(this.currentPlant.WANMqttPort);

    returnValue = !isNaN(LanPortNumber) && !isNaN(LANMqttPortNumner);

    if (returnValue) {
      returnValue =
        0 < LanPortNumber &&
        LanPortNumber < 65536 &&
        0 < LANMqttPortNumner &&
        LANMqttPortNumner < 65536;
    }

    if (
      this.currentPlant.AllowWanConnection &&
      this.currentPlant.WanPort !== ""
    ) {
      if (returnValue) {
        returnValue = !isNaN(WanPortNumber) && !isNaN(WANMqttPortNumber);
      }

      if (returnValue) {
        returnValue =
          0 < WanPortNumber &&
          WanPortNumber < 65536 &&
          0 < WANMqttPortNumber &&
          WANMqttPortNumber < 65536;
      }
    }

    return returnValue;
  }

  /**
   * Get connection state of current selected plant received from network service
   * and normalize it in css class with specific text
   *
   * @param plantConnectionState : connection state value received from network service
   */
  private normalizeConnectionState(plantConnectionState: ConnectionType): void {
    let connectionStateClassTmp: string = "";
    let connectionStateText: string = "";
    let isPlantConnectionAvaiable: boolean = false;
    let iconConnectionType: string = "globe-outline";

    switch (plantConnectionState) {
      case ConnectionType.NONE:
        /** Connection not avaiable */
        connectionStateClassTmp = "not-avaiable";
        connectionStateText = this._lblConnecitonStateNotAvaiable;
        break;
      case ConnectionType.LAN:
        /** Connection avaiable */
        iconConnectionType = "wifi-outline";
        connectionStateClassTmp = "avaiable";
        connectionStateText = this._lblConnectionStateAvaiable;

        isPlantConnectionAvaiable = true;
        break;

      /*NOTE:
      Peculiar case, used in two context:
        ** LAN-related and WAN-related "Address-block" are the same.
        ** when external choices force to consider ONLY ONE "address-block" between the LAN-related and WAN-related.
      */
      case ConnectionType.LANWAN:
        //iconConnectionType = "wifi-outline";
        connectionStateClassTmp = "avaiable";
        connectionStateText = this._lblConnectionStateAvaiable;

        isPlantConnectionAvaiable = true;
        break;

      case ConnectionType.WAN:
        connectionStateClassTmp = "avaiable";
        connectionStateText = this._lblConnectionStateAvaiable;

        isPlantConnectionAvaiable = true;
        break;
      default:
        /** Connection attempting to veryfing */
        connectionStateClassTmp = "waiting";
        connectionStateText = this._lblConnectionStateWaiting;
        break;
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "normalizeConnectionState()",
      "ConnectionStateText after normalisation: " +
        connectionStateText +
        " | Connection available: " +
        isPlantConnectionAvaiable
    );

    this._ngZone.run(() => {
      this.connectionStateClass = connectionStateClassTmp;
      this.connectionStateText = connectionStateText;
      this.isPlantConnectionAvaiable = isPlantConnectionAvaiable;
      this.iconConnectionType = iconConnectionType;
    });
  }

  /**
   * Set src image logo by application theme
   */
  private getSrcImageLogoByApplicationTheme(): void {
    //switch (this._clientSettings.theme) {
    switch (this._themeService.applicationTheme) {
      case ApplicationTheme.BIG:
        this.setBIGLogoInfo();

        break;

      case ApplicationTheme.DIGITAL_PLACE:
        this.setDigitalPlaceLogoInfo();
        break;
      default:
        break;
    }
  }

  /**
   * Set BIG logo info
   */
  private setBIGLogoInfo(): void {
    this.srcImageLogo = "assets/img/logo_semplice.png";
    this.heightLogo = 170;
    this.styleLogo = {};
  }

  /**
   * Set Digital place logo info
   */
  private setDigitalPlaceLogoInfo() {
    this.srcImageLogo = this._themeService.getLogoForLoginPage();
    this.heightLogo = 140;
    this.styleLogo = {
      margin: "60px 0px 60px 0px",
      width: "80%",
      height: "auto",
    };
  }
}
